import * as React from "react";
import { useWebSocket, ReadyState } from "@/lib/cjs-interop";
import { Loader } from "@/components/ui/loader";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { copyTextToClipboard } from "@/helpers/clipboard";

interface Props {
  conversationId: string;
}

interface GridSidebarComparisonEvent {
  type: "start" | "msg" | "end";
  t: string;
  conversation_id: string;
}

export const GridSidebarComparisonResponse: React.FC<Props> = ({ conversationId }) => {
  const [content, setContent] = React.useState<string>("");
  const [status, setStatus] = React.useState<"idle" | "running" | "completed">("idle");
  const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);
  const [copied, setCopied] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket<GridSidebarComparisonEvent>(
    "/pf-mainweb/business-reports/ws",
    {
      shouldReconnect: event => event?.code !== 1000,
      skipAssert: true,
      reconnectInterval: 3000,
    },
  );

  React.useEffect(() => {
    if (lastJsonMessage) {
      if (lastJsonMessage.conversation_id === conversationId) {
        if (lastJsonMessage.type === "msg") {
          setContent(prev => prev + lastJsonMessage.t);
        } else if (lastJsonMessage.type === "start") {
          setContent("");
          setStatus("running");
        } else if (lastJsonMessage.type === "end") {
          setStatus("completed");
        }
      }
    }
  }, [lastJsonMessage, setContent, setStatus, conversationId]);

  // Check if user is at bottom of scroll
  const isAtBottom = () => {
    if (!containerRef.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    return Math.abs(scrollHeight - clientHeight - scrollTop) < 5; // 5px tolerance
  };

  // Handle scroll events to detect user interaction
  const handleScroll = () => {
    setShouldAutoScroll(isAtBottom());
  };

  // Auto-scroll to bottom when content changes, but only if user is at bottom
  React.useEffect(() => {
    if (containerRef.current && shouldAutoScroll) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [content, shouldAutoScroll]);

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && conversationId) {
      sendJsonMessage({ query_id: conversationId });
    }
  }, [readyState, conversationId, sendJsonMessage]);

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        className="mb-4 ml-4 flex h-[100%] flex-col overflow-y-auto rounded-lg border border-gray-400 p-4 pt-2"
        onScroll={handleScroll}
      >
        <MarkdownRenderer content={content} evidences={[]} processCitations={true} />

        {status === "running" && <Loader />}
      </div>
      {content && status === "completed" && (
        <div className="mb-3 flex justify-end">
          <Button variant="second-white" size="sm" onClick={handleCopy} className="flex items-center gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy"}
          </Button>
        </div>
      )}
    </>
  );
};
